/**
 * MANUAL BPI VALIDATION DIALOG
 * Validation MANUELLE du BPI (bypass paiement) — ticket T1, réunion 25/08.
 *
 * Le staff atteste que le BPI est réglé alors qu'aucun paiement n'existe dans
 * le système (ancienne plateforme, invité exonéré, report d'édition) et saisit
 * le montant à défalquer du solde BED. 0 € est un choix VALIDE : le BPI est
 * réputé réglé mais rien n'est déduit, la totalité reste due au BED.
 *
 * Ce geste ne crée AUCUN paiement : ni dossier, ni encaissement, ni chiffre
 * d'affaires. Il lève seulement le verrou d'émission du lien BED et défalque
 * un acompte encaissé ailleurs.
 *
 * Modèle : ConfirmPaymentDialog (Dialog + Input/Textarea + toast Sonner).
 */

import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from '../ui';
import { useBpiManualValidationActions } from '../../hooks/useBpiManualValidation';
import type { BpiManualValidation } from '../../hooks/useBpiManualValidation';
import { formatCentsAsEurosInput, parseEurosToCents } from '../../lib/payment/manual-bpi-amount';

interface ManualBpiValidationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Porteur du dossier (un seul des deux, aligné PaymentSection).
  crewId?: string;
  contactId?: string;
  // Édition concernée. Inutile pour un équipage (le serveur lit
  // crews.product_id) ; requis pour un porteur contact.
  productId?: string;
  prospectName: string;
  // Validation déjà posée : le dialog passe en correction (montant pré-rempli).
  existingValidation?: BpiManualValidation | null;
  onValidated: () => void | Promise<void>;
}

export function ManualBpiValidationDialog({
  open,
  onOpenChange,
  crewId,
  contactId,
  productId,
  prospectName,
  existingValidation,
  onValidated,
}: ManualBpiValidationDialogProps) {
  const { validateBpiManually } = useBpiManualValidationActions();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Réinitialise à chaque ouverture (le dialog reste monté). En correction, on
  // pré-remplit avec les valeurs en place.
  useEffect(() => {
    if (!open) return;
    setAmount(existingValidation ? formatCentsAsEurosInput(existingValidation.amount_cents) : '');
    setNote(existingValidation?.note ?? '');
  }, [open, existingValidation]);

  const amountCents = parseEurosToCents(amount);
  const canSubmit = amountCents !== null && !submitting;

  async function handleSubmit() {
    if (amountCents === null) return;

    setSubmitting(true);
    const result = await validateBpiManually({
      crewId,
      contactId,
      amountCents,
      note,
      productId,
    });
    setSubmitting(false);

    if (!result.success) {
      toast.error(result.error ?? 'Erreur lors de la validation manuelle du BPI.');
      return;
    }

    toast.success(
      result.created
        ? 'BPI validé manuellement — le lien BED peut être généré.'
        : 'Validation manuelle du BPI mise à jour.'
    );
    onOpenChange(false);
    await onValidated();
  }

  function handleClose(value: boolean) {
    if (submitting) return;
    onOpenChange(value);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>
            {existingValidation
              ? 'Modifier la validation manuelle du BPI'
              : 'Valider le BPI manuellement'}
          </DialogTitle>
          <DialogDescription>
            Dossier <strong>{prospectName}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Cette action atteste que le BPI est réglé hors module de paiement (ancienne plateforme,
            invité exonéré, report d&apos;édition). Elle ne crée aucun encaissement : elle débloque
            la génération du lien BED et défalque le montant saisi du solde.
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700" htmlFor="manual-bpi-amount">
              Montant validé (€) <span className="text-red-500">*</span>
            </label>
            <Input
              id="manual-bpi-amount"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ex : 400"
              disabled={submitting}
            />
            <p className="text-xs text-zinc-400">
              Montant déduit du solde BED, exactement comme un acompte BPI réellement encaissé.
              Saisir <strong>0</strong> pour un dossier exonéré : la totalité reste due au BED.
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-zinc-700" htmlFor="manual-bpi-note">
              Motif
            </label>
            <Textarea
              id="manual-bpi-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ex : report 2026 → 2027, acompte de 400 € conservé."
              rows={3}
              disabled={submitting}
            />
            <p className="text-xs text-zinc-400">
              Le motif, l&apos;auteur et la date sont enregistrés dans la timeline du dossier.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)} disabled={submitting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {submitting ? 'Validation...' : 'Valider le BPI'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
